// What the recorder engine is built from, as interfaces: the browser's media
// objects, the api, timers and locks. The real ones are in recorderMedia.ts;
// the self-test passes fakes, so the engine's whole lifecycle (record,
// reload, lose the microphone, lose the network, sign out) runs in node.

import type { Meeting } from "@/api/meetings";
import type { RecordingFinishBody, RecordingStart, RecordingStartBody, RecordingUploadState } from "@/api/meetingRecordings";
import type { RecorderStore, TakeRecord } from "./recorderStore";
import type { CallSoundState, CallVia } from "./sourceModel";

export interface RecorderLike {
  readonly state: "inactive" | "recording" | "paused";
  readonly mimeType: string;
  ondataavailable: ((e: { data: Blob }) => void) | null;
  onstop: (() => void) | null;
  onerror: ((e: unknown) => void) | null;
  start(timeslice: number): void;
  stop(): void;
  requestData(): void;
}

export interface TrackLike {
  readonly readyState: "live" | "ended";
  readonly muted: boolean;
  stop(): void;
  addEventListener(type: "ended" | "mute" | "unmute", fn: () => void): void;
}

export interface StreamLike {
  getTracks(): TrackLike[];
  /** Set on a call recording (the call's sound mixed with the microphone: callSound.ts). */
  callSound?: CallSoundLike;
}

/** A call recording's two sides: what it still hears, a change of that, and sharing the call again. */
export interface CallSoundLike {
  /** Where the call side comes from: a share (callSound.ts; absent = "share") or the Mac app's own capture (nativeCallSound.ts). */
  via?: CallVia;
  state(): CallSoundState;
  onChange(fn: () => void): void;
  /** Share the call again into the same recording (it replaces a share still running); call straight from a tap. "blocked": the Mac does not allow it. */
  reshare(): Promise<"ok" | "cancelled" | "no_sound" | "blocked">;
  /** The call side has been silent for CALL_SILENT_SEC while the microphone heard speech. */
  callSilent?(): boolean;
  /** "denied": no call side is live and the last one ended because the Mac stopped allowing it. */
  lost?(): "denied" | null;
  /** The mix's own level meter (the engine then opens no second AudioContext); returns its stop. */
  meter?(onLevel: (v: number) => void): () => void;
}

export interface MediaDeps {
  supported(): boolean;
  /**
   * Must be the first thing awaited after a tap: iOS ties the permission to the gesture.
   * `callSound`: the call's sound (a shared tab or screen) mixed with the microphone.
   */
  getUserMedia(o?: { callSound?: boolean }): Promise<StreamLike>;
  pickMime(): string;
  createRecorder(stream: StreamLike, mime: string): RecorderLike;
  /** Calls onLevel (0..1) about ten times a second; returns its stop. */
  meter(stream: StreamLike, onLevel: (v: number) => void): () => void;
  /** Keeps the screen on while recording; returns its release. */
  wakeLock(): () => void;
  /** The microphone may be opened again without asking (a desktop with permission given). */
  micGranted(): Promise<boolean>;
}

export interface ApiDeps {
  start(customerId: string, body: RecordingStartBody): Promise<RecordingStart>;
  put(customerId: string, meetingId: string, uploadId: string, index: number, blob: Blob, opts: { keepalive?: boolean }): Promise<unknown>;
  finish(customerId: string, meetingId: string, uploadId: string, body: RecordingFinishBody): Promise<Meeting>;
  uploadState(customerId: string, meetingId: string, uploadId: string): Promise<RecordingUploadState>;
  meeting(customerId: string, meetingId: string): Promise<Meeting>;
  /** One recorder event for the server's log (metadata only, never audio); best effort, never throws. */
  diag(customerId: string, entry: RecorderDiag): void;
}

/** A recorder event, in the canonical diagnostics keys (platform web). */
export interface RecorderDiag {
  event: string;
  meeting_id?: string | null;
  upload_id?: string | null;
  client_ref?: string | null;
  segment?: number | null;
  parts?: number | null;
  seconds?: number | null;
  reason?: string | null;
  missing?: number[] | null;
}

export interface ClockDeps {
  now(): number;
  setTimeout(fn: () => void, ms: number): number;
  clearTimeout(id: number): void;
  setInterval(fn: () => void, ms: number): number;
  clearInterval(id: number): void;
}

export interface LockDeps {
  /** Whether the browser has Web Locks (else a take's owner is judged by its heartbeat). */
  readonly available: boolean;
  /** Take the lock if nobody holds it: its release, or null when another tab holds it. */
  hold(name: string): Promise<(() => void) | null>;
}

export interface EngineDeps {
  media: MediaDeps;
  api: ApiDeps;
  clock: ClockDeps;
  locks: LockDeps;
  store: () => Promise<RecorderStore>;
  uuid: () => string;
  tabId: string;
}

/** Why the upload is waiting, in one word the screen turns into a sentence. */
export type Waiting = "none" | "offline" | "signed_out" | "storage_full" | "refused";

/** A take held in this tab: its record plus what only lives in memory. */
export interface Runtime {
  rec: TakeRecord;
  stream: StreamLike | null;
  recorder: RecorderLike | null;
  stopMeter: (() => void) | null;
  releaseWake: (() => void) | null;
  releaseLock: (() => void) | null;
  /** Unsent parts (and part 0) by seq, the copy in memory. */
  blobs: Map<number, Blob>;
  /** Each part's write to the store; a part is sent only once it is stored. */
  stored: Map<number, Promise<void>>;
  /** Parts whose write to the store failed: only in this page until the server has them. */
  unstored: Set<number>;
  /** Parts the server said it is missing, sent again before the finish. */
  resend: number[];
  /** Part 0 has gone to the current upload (only matters for a moved upload). */
  headerSent: boolean;
  /** Finish with allow_gaps: a part is gone from the device. */
  gaps: boolean;
  sending: boolean;
  finishing: boolean;
  attempt: number;
  timer: number | null;
  waiting: Waiting;
  refusal: string | null;
  /** The upload a wrong_upload moved this take away from. */
  movedFrom: string | null;
  /** The server handed back the upload it had just refused (a hiccup): resumed once, then waited on. */
  bounced: number;
  /** Stop was pressed: the recorder's last piece is awaited, not read as the microphone going. */
  stopping: boolean;
  /** The recorder's last piece has arrived (or never will): the take may finish. */
  finalIn: boolean;
  stopWaiter: (() => void) | null;
  mutedSince: number | null;
  bytesSent: number;
  /** Started in this page (its outcome is shown); false for one taken over from a closed page. */
  shown: boolean;
  /** The microphone went and may carry on by itself any second: not finished until that is settled. */
  carryPending: boolean;
}

export function newRuntime(rec: TakeRecord, shown: boolean): Runtime {
  return {
    rec, stream: null, recorder: null, stopMeter: null, releaseWake: null, releaseLock: null,
    blobs: new Map(), stored: new Map(), unstored: new Set(), resend: [], headerSent: false, gaps: false,
    sending: false, finishing: false, attempt: 0, timer: null, waiting: "none", refusal: null,
    movedFrom: null, bounced: 0, stopping: false, finalIn: rec.status !== "capturing", stopWaiter: null,
    mutedSince: null, bytesSent: 0, shown, carryPending: false,
  };
}

/** The HTTP status and server code of a failed call (status 0: no answer). */
export function failureOf(e: unknown): { status: number; code: string | null; missing: number[] | null } {
  const err = e as { status?: unknown; code?: unknown; body?: unknown } | null;
  const status = typeof err?.status === "number" ? err.status : 0;
  let code = typeof err?.code === "string" ? err.code : null;
  let missing: number[] | null = null;
  const detail = (err?.body as { detail?: { error?: unknown; missing?: unknown } } | undefined)?.detail;
  if (detail && typeof detail === "object") {
    if (typeof detail.error === "string") code = detail.error;
    if (Array.isArray(detail.missing)) missing = detail.missing.filter((n): n is number => Number.isInteger(n));
  }
  return { status, code, missing };
}
